import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import axios from 'axios'
import { API_BASE_URL } from '../../config'

const baseUrl = API_BASE_URL

/**
 * Gate for every student area that requires a fully activated account.
 *
 * Flow the client asked for:
 *   register -> verify email -> account still PENDING -> enter activation code -> access
 *
 * Before this existed, only Login.jsx redirected pending students to /student/activate,
 * so anyone who typed /student/dashboard directly (or came back with a stale
 * studentLoginStatus in localStorage) walked straight past the activation step.
 *
 * localStorage is treated as a fast hint only — a student can edit it — so the real
 * answer is re-checked against the server on mount and localStorage is corrected.
 */
const RequireActivation = ({ children }) => {
    const loggedIn = localStorage.getItem('studentLoginStatus') === 'true'
    const studentId = localStorage.getItem('studentId')
    // null = still checking, true/false = server answered
    const [serverActivated, setServerActivated] = useState(null)

    useEffect(() => {
        if (!loggedIn || !studentId) return
        let cancelled = false

        axios.get(`${baseUrl}/student/${studentId}/`)
            .then(res => {
                if (cancelled) return
                const activated = !!res.data?.is_activated
                localStorage.setItem('studentActivated', activated ? 'true' : 'false')
                setServerActivated(activated)
            })
            .catch(() => {
                // Network/API failure: fall back to the cached value rather than
                // locking a legitimately activated student out of their account.
                if (cancelled) return
                setServerActivated(localStorage.getItem('studentActivated') === 'true')
            })

        return () => { cancelled = true }
    }, [loggedIn, studentId])

    if (!loggedIn || !studentId) {
        return <Navigate to='/student/login' replace />
    }

    // The server is authoritative once it answers — this catches a tampered localStorage.
    if (serverActivated === false) {
        return <Navigate to='/student/activate' replace />
    }

    // Server hasn't answered yet: trust the cache, but stay pessimistic when it says
    // pending, so a pending student never sees the dashboard even for a moment.
    if (serverActivated === null && localStorage.getItem('studentActivated') !== 'true') {
        return <Navigate to='/student/activate' replace />
    }

    return children
}

export default RequireActivation
